<template>
  <div>
    <v-row>
      <v-col cols="4">
        <v-autocomplete
          :label="businessRulesGetBoolean('Jobs Rename to Release') ? 'Release' : 'Job'"
          v-model="jobFilterId"
          :items="jobs"
          hide-details
          item-value="id"
          :item-text="item => `${item.code} - ${item.name}; ${item.reference}`"
          clearable
          :loading="loadingJobs"
        >
          <template #selection="{ item }">
            <span>
              <span style="font-weight: bold;">{{ item.code ? `${item.code}` : '' }}</span> - {{ item.name ? item.name : '' }} <span style="color: #1976D2;">{{ item.reference ? `; ${item.reference}` : '' }}</span>
            </span>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="2">
        <v-select 
          label="Requested By" 
          hide-details 
          v-model="requestedByFilter" 
          :items="users" 
          :item-text="item => `${item.first_name} ${item.last_name}`"
          item-value="id" 
          clearable/>
      </v-col>

      <v-col cols="2">
        <v-switch
          v-model="showArchivedFilter"
          hide-details
          label="Show Archived"
        />
      </v-col>

      <v-col  class="text-right pt-7">
        <v-btn color="primary" outlined @click="openRequestMaterialForm">
          <v-icon small class="mr-1">
            fas fa-plus
          </v-icon>
          Request Material
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="tableHeaders"
          :items="tableData"
          :search="search"
          :footer-props="footer"
          :items-per-page="pagination.itemsPerPage"
          :loading="tableIsLoading"
          dense
          fixed-header
          :server-items-length="numberOfRecords"
          @update:options="updateOptions"
        >
          <template #item="{ item }">
            <tr>
              <td>{{ item.allocation_item.allocation.id }}</td>
              <td>
                <span v-if="item.allocation_item.allocation.job">  
                  <span style="font-weight: bold;">
                    <text-highlight :queries="search">{{ item.allocation_item.allocation.job.code ? `${item.allocation_item.allocation.job.code}` : '' }}</text-highlight>
                  </span>
                  - 
                  <text-highlight :queries="search">{{ item.allocation_item.allocation.job.project ? item.allocation_item.allocation.job.project.name : '' }}</text-highlight>
                  <span style="color: #1976D2;">
                    <text-highlight :queries="search">{{ item.allocation_item.allocation.job.reference ? `; ${item.allocation_item.allocation.job.reference}` : '' }}</text-highlight>
                  </span>
                </span>
              </td>
              <td>
                <span v-if="item.allocation_item.inventory_item.classes">
                  <v-chip class="mr-1" small text-color="white" :color="item.allocation_item.inventory_item.classes.color">
                    {{ item.allocation_item.inventory_item.classes.name }}
                  </v-chip>
                </span>
                <span v-if="item.allocation_item.inventory_item.sub_classes">
                  <v-chip v-for="subclass in item.allocation_item.inventory_item.sub_classes" class="mr-1" small text-color="white" :color="subclass.color">
                    {{ subclass.name }}
                  </v-chip>
                </span>
                <span style="font-weight:bold">{{ item.allocation_item.inventory_item.material_id }}</span>:
                {{ item.allocation_item.inventory_item.name }}
              </td>
              <td>{{ item.allocation_item.uom.name }}</td>
              <td>{{ item.allocation_item.qty_required }}</td>
              <td>{{ item.qty_supplied }}</td>
              <td class="text-center">
                <v-tooltip v-if="item.reason_name" top color="black">
                  <template #activator="{ on }">
                    <v-icon v-on="on" x-small>mdi-eye</v-icon>
                  </template>
                  {{ item.reason_name }}
                </v-tooltip>
              </td>
              <td>{{ item.requested_by.first_name }} {{ item.requested_by.last_name }}</td>
              <td>{{ item.created_at }}</td>
              <td><span v-if="item.reviewed_by">{{ item.reviewed_by.first_name }} {{ item.reviewed_by.last_name }}</span></td>
              <td>
                  <v-chip 
                    v-if="item.status_id in statusMap" 
                    small 
                    text-color="white" 
                    :color="statusMap[item.status_id].color" 
                    style="text-transform: uppercase;">
                      {{ statusMap[item.status_id].name }}
                  </v-chip>
              </td>
              <td class="text-right">
                <v-menu offset-y>
                  <template #activator="{ on, attrs }">
                    <v-btn class="ml-1 my-1" elevation="0" fab outlined x-small v-bind="attrs" tile color="primary" style="border-radius: 7px; width: 28px !important; height: 28px !important;" v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list class="pa-3">

                    <v-list-item :disabled="!canDelete(item)">
                      <v-list-item-title 
                        @click="deleteMaterialRequest(item)" 
                        :style="`color: ${canDelete(item) ? 'red' : 'grey'}; font-weight: 600; cursor:${item.status_id === 1 ? 'pointer' : 'default'}`" 
                      >
                        DELETE
                      </v-list-item-title>
                    </v-list-item>


                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="showRequestMaterialForm" max-width="66%" persistent>
      <inventory-material-request-form 
        @on-material-requested="loadTableData()"
        @close="showRequestMaterialForm = false"
      />
    </v-dialog>

    <confirm-form ref="confirmForm" />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import InventoryClassSelectElement from '@/components/elements/InventoryClassSelectElement.vue';
import InventoryMaterialRequestForm from '@/components/forms/inventoryAllocation/InventoryMaterialRequestForm.vue';
import * as allocationApi from '@/api/InventoryAllocationApi.js';

export default {
  components: {
    InventoryClassSelectElement,
    InventoryMaterialRequestForm,
  },
  data: () => ({
    showRequestMaterialForm: false,
    requestedByFilter: null,
    showArchivedFilter: false,
    jobFilterId: null,
    jobs: [],
    users: [],
    loadingJobs: false,
    footer: {
      'items-per-page-options': [20, 50, 100, 500, 1000],
    },
    tableIsLoading: false,
    tableData: [],
    statusMap: {},
    numberOfRecords: 0,
    pagination: {
      page: 1,
      itemsPerPage: 25
    },
    tableHeaders: [
      { text: 'Allocation ID', value: 'allocation_id', sortable: true, value: 'allocation_id' },
      { text: 'Release', value: 'job_code' , sortable: true},
      { text: 'Item', value: 'inventory_item_material_id' , sortable: true },
      { text: 'UOM', value: 'uom_id' , sortable: false,},
      { text: 'Qty Required', value: 'qty_required' , sortable: true,},
      { text: 'Qty Supplied', value: 'qty_supplied' , sortable: false,},
      { text: 'Reason', sortable: false },
      { text: 'Requested By', sortable: false },
      { text: 'Requested At', value: 'created_at' , sortable: false,},
      { text: 'Approved By', sortable: false },
      { text: 'Status', sortable: false },
      { text: 'Actions', sortable: false },

    ],
    sort: {
      sortBy: 'created_at',
      sortDirection: 'desc'
    },
    selectedTab: 'forms',
  }),
  computed: {
    ...mapState({
      search: (state) => state.search,
      user: (state) => state.user,
  }),
    ...mapGetters([
      'businessRulesGetBoolean',
      'getAdminModuleValue',
      'checkUserPermission',
    ]),
    isAuthorized() {
      return this.user.roles.some(x => x.name === 'Allocation Admin' || x.name === 'Admin' || x.name === 'Material Handler' || x.name === 'Material Requester');
    },
  },
  watch: {
    pagination() {
      if (!this.tableIsLoading) this.loadTableData();
    }, 
    sort() {
      if (!this.tableIsLoading) this.loadTableData();
    },
    search() {
      this.loadTableData();
    },
    jobFilterId() {
      this.loadTableData();
    },
    requestedByFilter() {
      this.loadTableData();
    },
    showArchivedFilter() {
      this.loadTableData();
    },
  },
  created() {
    allocationApi.getRequestMaterialStatuses().then(res => {
      console.log(res)
      this.statusMap = res.reduce((acc, x) => {
        acc[x.id] = x;
        return acc;
      }, {})
    });
    if (!this.isAuthorized) this.$router.push('/');
    this.init();
  },
  methods: {
    async init() {
      Promise.all([
        this.$api.job.readDDL(),
        this.$api.user.getList(),
        this.loadTableData()
      ]).then(([jobs, users]) => {
        this.jobs = jobs;
        this.users = users;
      });
    },

    async loadTableData() {
      try {
        this.tableIsLoading = true;
        const data = {
          filters: {
            job_id: this.jobFilterId,
            requested_by_id: this.requestedByFilter,
            show_archived: this.showArchivedFilter,
          },
          pagination: this.pagination,
          sort: this.sort,
          search: this.search
        };

        const materialRes = await allocationApi.readRequestedMaterials(data);
        console.log(materialRes.data)
        this.tableData = materialRes.data;
        this.numberOfRecords = materialRes.total;
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', 'There was an error loading table data');
      } finally {
        this.tableIsLoading = false;
      }
    },

    openRequestMaterialForm() {
      this.showRequestMaterialForm = true;
    },

    updateOptions(newOptions) {
      this.pagination = newOptions;
      this.sort = {
        sortBy: newOptions.sortBy[0],
        sortDirection: newOptions.sortDesc[0],
      };
    },

    canDelete(request) {
      return request.status_id === 1 && (this.checkUserPermission(['Admin', 'Allocation Admin']) || this.user.id == request.requested_by_id);
    },

    async deleteMaterialRequest(materialRequest) {
      try {
        if (!this.canDelete(materialRequest)) return;
        const confirm = await this.$refs.confirmForm.open({ title: 'Are you sure that you want to approve this Request?' });
        if (!confirm) return;
        const res = await allocationApi.deleteMaterialRequest(materialRequest.id);
        this.$bus.$emit('showSuccess', `Request has been deleted`);
        this.tableData = this.tableData.filter(x => x.id !== materialRequest.id);
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', `Failed to delete Material Request`);
      }
    }
  },
};
</script>